import store from '@/stores';

export type AuthSessionLogReason =
  | 'auth'
  | 'transient'
  | 'user'
  | 'bootstrap'
  | 'admin'
  | 'unknown';

export type AuthSessionLogLevel = 'info' | 'warn' | 'error';

export type AuthSessionLogEvent = {
  ts: string;
  level: AuthSessionLogLevel;
  event: string;
  reason?: AuthSessionLogReason;
  trigger?: string;
  path?: string;
  httpStatus?: number;
  online?: boolean;
  visibility?: DocumentVisibilityState;
  accessExpiresInMs?: number | null;
  userId?: number | null;
  detail?: string;
};

const LOG_PREFIX = '[auth-session]';
const STORAGE_KEY = 'roomi-meet-auth-session-log';
const MAX_RING_SIZE = 50;

type AuthSessionLogGlobal = {
  __roomiAuthSessionLog?: AuthSessionLogEvent[];
};

function getAccessExpiresInMs(): number | null {
  const expiresAt = store.getState().authenticated.accessTokenExpiresAt;
  if (typeof expiresAt !== 'number' || !Number.isFinite(expiresAt)) {
    return null;
  }
  return expiresAt - Date.now();
}

function getUserId(): number | null {
  const userId = store.getState().authenticated.userId;
  return typeof userId === 'number' && Number.isFinite(userId) ? userId : null;
}

function readRing(): AuthSessionLogEvent[] {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw) as AuthSessionLogEvent[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeRing(events: AuthSessionLogEvent[]): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(events));
  } catch {
    // sessionStorage 不可時は console のみ
  }
}

function appendToRing(event: AuthSessionLogEvent): void {
  const ring = readRing();
  ring.push(event);
  if (ring.length > MAX_RING_SIZE) {
    ring.splice(0, ring.length - MAX_RING_SIZE);
  }
  writeRing(ring);
}

function emitToConsole(event: AuthSessionLogEvent): void {
  const payload = { ...event };
  if (event.level === 'error') {
    console.error(LOG_PREFIX, payload);
    return;
  }
  if (event.level === 'warn') {
    console.warn(LOG_PREFIX, payload);
    return;
  }
  console.info(LOG_PREFIX, payload);
}

export function authSessionLog(
  partial: Omit<AuthSessionLogEvent, 'ts' | 'online' | 'visibility' | 'accessExpiresInMs' | 'userId'> &
    Partial<Pick<AuthSessionLogEvent, 'online' | 'visibility' | 'accessExpiresInMs' | 'userId'>>,
): AuthSessionLogEvent {
  const event: AuthSessionLogEvent = {
    ts: new Date().toISOString(),
    online: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
    visibility: typeof document !== 'undefined' ? document.visibilityState : undefined,
    accessExpiresInMs: getAccessExpiresInMs(),
    userId: getUserId(),
    ...partial,
  };

  appendToRing(event);
  emitToConsole(event);
  return event;
}

export function dumpAuthSessionLog(): AuthSessionLogEvent[] {
  return readRing();
}

export function dumpAuthSessionLogText(): string {
  return JSON.stringify(readRing(), null, 2);
}

declare global {
  interface Window {
    __roomiAuthSessionLog?: () => AuthSessionLogEvent[];
    __roomiAuthSessionLogText?: () => string;
  }
}

if (typeof window !== 'undefined') {
  window.__roomiAuthSessionLog = dumpAuthSessionLog;
  window.__roomiAuthSessionLogText = dumpAuthSessionLogText;
}
